import { CONFIG } from "../config.js";

/**
 * Data Processor Service
 * Handles data filtering, aggregation, and calculations
 */

/**
 * Filters transactions based on criteria
 * @param {Array} transactions - Array of transactions
 * @param {Object} filters - Filter criteria
 * @returns {Array} Filtered transactions
 */
export function filterTransactions(transactions, filters = {}) {
  return transactions.filter((transaction) => {
    // Filter by year
    if (filters.year) {
      const transactionYear = new Date(transaction.date).getFullYear();
      if (transactionYear !== parseInt(filters.year)) {
        return false;
      }
    }

    // Filter by month
    if (filters.month) {
      const transactionMonth = new Date(transaction.date).getMonth() + 1;
      if (transactionMonth !== parseInt(filters.month)) {
        return false;
      }
    }

    // Filter by project
    if (filters.project && transaction.project !== filters.project) {
      return false;
    }

    // Filter by account type
    if (
      filters.accountType &&
      transaction.accountType !== filters.accountType
    ) {
      return false;
    }

    // Filter by category
    if (filters.category && transaction.category !== filters.category) {
      return false;
    }

    // Filter by type (income/expense)
    if (filters.type && transaction.type !== filters.type) {
      return false;
    }

    return true;
  });
}

/**
 * Calculates summary statistics from transactions
 * @param {Array} transactions - Array of transactions
 * @returns {Object} Summary with totalIncome, totalExpense, netBalance
 */
export function calculateSummary(transactions) {
  let totalIncome = 0;
  let totalExpense = 0;

  transactions.forEach((transaction) => {
    const amount = parseFloat(transaction.amount) || 0;
    const currency = transaction.currency || CONFIG.DEFAULT_CURRENCY;
    const exchangeRate = parseFloat(transaction.exchangeRate) || 1;

    // Convert to Kip
    const amountInKip = currency === "₭" ? amount : amount * exchangeRate;

    if (transaction.type === "ລາຍຮັບ") {
      totalIncome += amountInKip;
    } else if (transaction.type === "ລາຍຈ່າຍ") {
      totalExpense += amountInKip;
    }
  });

  return {
    totalIncome,
    totalExpense,
    netBalance: totalIncome - totalExpense,
  };
}

/**
 * Groups transactions by category
 * @param {Array} transactions - Array of transactions
 * @returns {Object} Transactions grouped by category
 */
export function groupByCategory(transactions) {
  const grouped = {};

  transactions.forEach((transaction) => {
    const category = transaction.category || "ອື່ນໆ";
    const amount = parseFloat(transaction.amount) || 0;
    const currency = transaction.currency || CONFIG.DEFAULT_CURRENCY;
    const exchangeRate = parseFloat(transaction.exchangeRate) || 1;
    const amountInKip = currency === "₭" ? amount : amount * exchangeRate;

    if (!grouped[category]) {
      grouped[category] = {
        category,
        type: transaction.type,
        total: 0,
        count: 0,
        transactions: [],
      };
    }

    grouped[category].total += amountInKip;
    grouped[category].count += 1;
    grouped[category].transactions.push(transaction);
  });

  return grouped;
}

/**
 * Groups transactions by project
 * @param {Array} transactions - Array of transactions
 * @returns {Object} Transactions grouped by project
 */
export function groupByProject(transactions) {
  const grouped = {};

  transactions.forEach((transaction) => {
    const project = transaction.project || "ບໍ່ລະບຸໂຄງການ";
    const amount = parseFloat(transaction.amount) || 0;
    const currency = transaction.currency || CONFIG.DEFAULT_CURRENCY;
    const exchangeRate = parseFloat(transaction.exchangeRate) || 1;
    const amountInKip = currency === "₭" ? amount : amount * exchangeRate;

    if (!grouped[project]) {
      grouped[project] = {
        project,
        income: 0,
        expense: 0,
        balance: 0,
        transactions: [],
      };
    }

    if (transaction.type === "ລາຍຮັບ") {
      grouped[project].income += amountInKip;
    } else if (transaction.type === "ລາຍຈ່າຍ") {
      grouped[project].expense += amountInKip;
    }

    grouped[project].balance =
      grouped[project].income - grouped[project].expense;
    grouped[project].transactions.push(transaction);
  });

  return grouped;
}

/**
 * Groups transactions by month
 * @param {Array} transactions - Array of transactions
 * @returns {Array} Monthly data with income and expense
 */
export function groupByMonth(transactions) {
  const grouped = {};

  transactions.forEach((transaction) => {
    const date = new Date(transaction.date);
    const year = date.getFullYear();
    const month = date.getMonth();
    const key = `${year}-${String(month + 1).padStart(2, "0")}`;

    const amount = parseFloat(transaction.amount) || 0;
    const currency = transaction.currency || CONFIG.DEFAULT_CURRENCY;
    const exchangeRate = parseFloat(transaction.exchangeRate) || 1;
    const amountInKip = currency === "₭" ? amount : amount * exchangeRate;

    if (!grouped[key]) {
      grouped[key] = {
        year,
        month,
        monthName: CONFIG.LAO_MONTHS[month],
        income: 0,
        expense: 0,
        balance: 0,
      };
    }

    if (transaction.type === "ລາຍຮັບ") {
      grouped[key].income += amountInKip;
    } else if (transaction.type === "ລາຍຈ່າຍ") {
      grouped[key].expense += amountInKip;
    }

    grouped[key].balance = grouped[key].income - grouped[key].expense;
  });

  // Convert to array and sort by date
  return Object.values(grouped).sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    return a.month - b.month;
  });
}

/**
 * Gets top categories by spending
 * @param {Array} transactions - Array of transactions
 * @param {number} limit - Number of top categories to return
 * @returns {Array} Top categories
 */
export function getTopCategories(transactions, limit = 5) {
  const grouped = groupByCategory(transactions);

  return Object.values(grouped)
    .sort((a, b) => b.total - a.total)
    .slice(0, limit);
}

/**
 * Sorts transactions by date
 * @param {Array} transactions - Array of transactions
 * @param {string} order - 'asc' or 'desc'
 * @returns {Array} Sorted transactions
 */
export function sortByDate(transactions, order = "desc") {
  return [...transactions].sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return order === "desc" ? dateB - dateA : dateA - dateB;
  });
}
